import React, { useEffect, useState } from "react";
import { Cloud, CloudOff, Loader, XCircle, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { syncEngine } from "../../utils/syncEngine";
import { getQueueStats } from "../../utils/offlineQueue";

/**
 * SyncStatusIndicator Component
 *
 * Shows sync status in the UI
 * - Syncing animation when processing
 * - Queue count when pending
 * - Success/failure feedback
 */
export const SyncStatusIndicator: React.FC = () => {
  const { t } = useTranslation("pwa");
  const [isSyncing, setIsSyncing] = useState(false);
  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    succeeded: 0,
    failed: 0,
  });
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    // Listen to sync progress
    const listener = (prog: any) => {
      setIsSyncing(syncEngine.isSyncing());
      setProgress({
        total: prog.total,
        processed: prog.processed,
        succeeded: prog.succeeded,
        failed: prog.failed,
      });
    };

    syncEngine.addListener(listener);

    // Update queue count periodically
    const updateQueue = async () => {
      const stats = await getQueueStats();
      setQueueCount(stats.pending);
    };

    updateQueue();
    const interval = setInterval(updateQueue, 5000);

    return () => {
      syncEngine.removeListener(listener);
      clearInterval(interval);
    };
  }, []);

  if (!isSyncing && queueCount === 0) {
    return null; // Nothing to show
  }

  if (isSyncing) {
    return (
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50">
        <Loader className="h-5 w-5 animate-spin" />
        <div>
          <p className="text-sm font-semibold">{t("sync.syncing")}</p>
          <p className="text-xs opacity-90">
            {t("sync.syncingProgress", {
              processed: progress.processed,
              total: progress.total,
            })}
          </p>
        </div>
      </div>
    );
  }

  if (queueCount > 0) {
    return (
      <div className="fixed bottom-4 right-4 bg-orange-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 z-50">
        <CloudOff className="h-5 w-5" />
        <div>
          <p className="text-sm font-semibold">
            {t("sync.pendingChanges", { count: queueCount })}
          </p>
          <p className="text-xs opacity-90">{t("sync.willSyncWhenOnline")}</p>
        </div>
      </div>
    );
  }

  return null;
};

/**
 * SyncButton Component
 *
 * Manual sync trigger button
 */
interface SyncButtonProps {
  onSyncStart?: () => void;
  onSyncComplete?: () => void;
  className?: string;
}

export const SyncButton: React.FC<SyncButtonProps> = ({
  onSyncStart,
  onSyncComplete,
  className = "",
}) => {
  const { t } = useTranslation("pwa");
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueCount, setQueueCount] = useState(0);

  useEffect(() => {
    const updateQueue = async () => {
      const stats = await getQueueStats();
      setQueueCount(stats.pending);
    };

    updateQueue();
    const interval = setInterval(updateQueue, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleSync = async () => {
    if (!navigator.onLine) {
      alert(t("sync.cannotSyncOffline"));
      return;
    }

    if (isSyncing) return;

    try {
      setIsSyncing(true);
      onSyncStart?.();

      await syncEngine.startSync();

      onSyncComplete?.();
    } catch (error) {
      console.error("[Sync] Manual sync failed:", error);
      alert(t("sync.syncFailed"));
    } finally {
      setIsSyncing(false);
    }
  };

  if (queueCount === 0) {
    return null; // Nothing to sync
  }

  return (
    <button
      onClick={handleSync}
      disabled={isSyncing || !navigator.onLine}
      className={`flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {isSyncing ? (
        <>
          <Loader className="h-4 w-4 animate-spin" />
          <span>{t("sync.syncingButton")}</span>
        </>
      ) : (
        <>
          <Cloud className="h-4 w-4" />
          <span>{t("sync.syncButton", { count: queueCount })}</span>
        </>
      )}
    </button>
  );
};

/**
 * QueuedActionsPanel Component
 *
 * Shows list of queued actions (for debugging or user awareness)
 */
export const QueuedActionsPanel: React.FC = () => {
  const { t } = useTranslation("pwa");
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    failed: 0,
    total: 0,
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const updateStats = async () => {
      const queueStats = await getQueueStats();
      setStats(queueStats);
    };

    updateStats();
    const interval = setInterval(updateStats, 3000);

    return () => clearInterval(interval);
  }, []);

  if (stats.total === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-20 right-4 z-50">
      {!isOpen ? (
        <button
          onClick={() => setIsOpen(true)}
          className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Cloud className="h-6 w-6 text-gray-600" />
              {stats.pending > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {stats.pending}
                </span>
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">
                {t("queue.title")}
              </p>
              <p className="text-xs text-gray-500">
                {t("queue.actionsCount", { count: stats.total })}
              </p>
            </div>
          </div>
        </button>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-80">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("queue.panelTitle")}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <span className="text-sm text-gray-700">
                  {t("queue.pending")}
                </span>
              </div>
              <span className="text-sm font-semibold text-gray-900">
                {stats.pending}
              </span>
            </div>

            {stats.processing > 0 && (
              <div className="flex items-center justify-between p-2 bg-blue-50 rounded">
                <div className="flex items-center space-x-2">
                  <Loader className="h-4 w-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-gray-700">
                    {t("queue.processing")}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.processing}
                </span>
              </div>
            )}

            {stats.failed > 0 && (
              <div className="flex items-center justify-between p-2 bg-red-50 rounded">
                <div className="flex items-center space-x-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-gray-700">
                    {t("queue.failed")}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-900">
                  {stats.failed}
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200">
            <SyncButton className="w-full" />
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * PendingActionBadge Component
 *
 * Small badge to show pending actions count
 */
export const PendingActionBadge: React.FC = () => {
  const { t } = useTranslation("pwa");
  const [count, setCount] = useState(0);

  useEffect(() => {
    const updateCount = async () => {
      const stats = await getQueueStats();
      setCount(stats.pending);
    };

    updateCount();
    const interval = setInterval(updateCount, 5000);

    return () => clearInterval(interval);
  }, []);

  if (count === 0) return null;

  return (
    <div className="inline-flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
      <CloudOff className="h-3 w-3" />
      <span>{t("queue.pendingBadge", { count })}</span>
    </div>
  );
};
